import { Link, useRoute } from "wouter";
import { ChevronLeft, ChevronRight, ShieldCheck, Building2 } from "lucide-react";
import { api } from "../lib/api";
import type { OrgProfilePayload } from "../lib/api";
import { useAuth } from "../lib/auth";

import { CompanyCoverField } from "../components/CompanyCoverField";

import { CompanyVerificationFields } from "../components/CompanyVerificationFields";
import { companyProofError } from "../lib/companyProof";

import "../components/CompanyManagementMobile.css";
import { STEPS, COMPANY_FLOW_VERSION, profileIssue } from "./company-wizard/model";
import { CompanyProgress } from "./company-wizard/FieldControls";
import { StepBasics, StepLocation, StepProfile } from "./company-wizard/BusinessDetails";
import { StepRoles, StepIndustry, StepReview } from "./company-wizard/BusinessScope";
import { useCompanyWizardDraft } from "./company-wizard/useCompanyWizardDraft";
export default function CompanyWizard() {
  const { user, loading } = useAuth();
  const [, editParams] = useRoute("/company/:id/edit");
  if (loading) return <div className="container-x py-16 text-center text-mut">Loading business profile…</div>;
  return <CompanyWizardForm key={`${COMPANY_FLOW_VERSION}:${user?.id ?? "guest"}:${user?.activeContext || "personal"}:${editParams?.id || "create"}`} />;
}

function CompanyWizardForm() {
 const { editId, navigate, returnTo, user, authLoading, tax, form, companyId, setCompanyId, step, setStep, mobileViewport, busy, setBusy, err, setErr, loading, enabling, subtypeOther, setSubtypeOther, savedSubtype, legacyExtras, logoFile, setLogoFile, logoUploaded, setLogoUploaded, coverFile, coverUploaded, setCoverUploaded, coverRemoved, setCoverRemoved, existingCoverUrl, setExistingCoverUrl, proofType, setProofType, proofFile, setProofFile, proofSubmissionId, setProofSubmissionId, needsProof, proofError, setProofError, submitting, issue, setIssue, section, saving, chooseLogo, chooseCover, locationDraft, setLocationDraft, pendingTokens, setPendingTokens, snapshot, draft, set, canPromote, enablePromote, selectedType } = useCompanyWizardDraft();
if (authLoading) return <div className="container-x py-16 text-center text-mut">Loading business profile…</div>;
  if (!user) { navigate("/login"); return null; }
  if (loading || !draft.ready) return <div className="container-x py-16 text-center text-mut">Loading business profile…</div>;
  if (!tax) return <div className="container-x py-16 text-center text-brand-red">{err || "Couldn't load the taxonomy."}</div>;

  // Capability gate: creating a business profile needs the "Promote a Company"
  // activity. Instead of a dead-end 403, explain why and offer to enable it here.
  if (!editId && !canPromote) {
    return (
      <div className="container-x py-10 max-w-xl">
        <div className="card p-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-brand-gold/15 text-navy grid place-items-center mx-auto mb-4"><Building2 className="w-7 h-7" /></div>
          <h1 className="text-xl font-extrabold text-navy mb-1">Enable business posting</h1>
          <p className="text-mut text-sm mb-1">Registering a Business Profile needs the <b>“Promote a Company”</b> activity on your account. It isn't switched on yet — that's why posting was blocked.</p>
          <p className="text-mut text-sm mb-5">Turning it on is free and instant. You keep all your other activities; this just lets you create and manage organizations.</p>
          {err && <div className="mb-4 rounded-lg bg-brand-red/10 text-brand-red text-sm px-4 py-2.5">{err}</div>}
          <div className="flex items-center justify-center gap-2">
            <button onClick={enablePromote} disabled={enabling} className="btn-red h-10 px-5 disabled:opacity-60">
              <Building2 className="w-4 h-4" />{enabling ? "Enabling…" : "Enable & continue"}
            </button>
            <button onClick={() => navigate(returnTo)} className="btn-outline h-10 px-4">Not now</button>
          </div>
          <p className="text-xs text-mut mt-4">You can also manage activities anytime from your <span className="text-navy font-medium">Dashboard</span>.</p>
        </div>
      </div>
    );
  }

  // Persist: create on first save, PATCH thereafter. `complete` marks the finished wizard.
  async function persist(complete: boolean): Promise<string | null> {
    if (saving.current) return null;
    saving.current = true;
    setBusy(true); setErr(null);
    try {
      // v3.9.67 (QA #2) — an "Other — specify" subtype sends the typed value as the subtype.
      const subtype = /other|specify/i.test(form.subtype) && subtypeOther.trim() ? subtypeOther.trim() : form.subtype;
      const pendingCover = Boolean((coverFile && !coverUploaded) || coverRemoved);
      // [business.media](../../../docs/product/businesses.md#business-media): a failed cover write stays recoverable and prevents completing the profile.
      const payload: OrgProfilePayload = { ...form, subtype, complete: complete && !pendingCover };
      let id = companyId;
      if (id) {
        await api.updateCompanyProfile(id, payload);
      } else {
        const r = await api.createCompany(payload);
        id = r?.item?.id || "";
        if (id) { draft.checkpoint({ ...snapshot, companyId: id }); setCompanyId(id); }
      }
      // v3.9.69 (QA #62) — upload the chosen logo once the company exists. Non-fatal:
      // the profile still saves if the logo upload fails.
      if (id && logoFile && !logoUploaded) {
        try { await api.uploadCompanyLogo(id, logoFile); setLogoUploaded(true); }
        catch { /* keep going — logo can be added later from company settings */ }
      }
      if (id && pendingCover) {
        try {
          if (coverFile) {
            await api.uploadCompanyCover(id, coverFile);
            setCoverUploaded(true);
            setExistingCoverUrl(`${api.companyCoverUrl(id)}?v=${Date.now()}`);
          } else {
            await api.removeCompanyCover(id);
            setExistingCoverUrl(""); setCoverRemoved(false);
          }
        } catch {
          setErr("Your business details were saved, but the cover photo could not be saved. Retry, or remove the selected photo to continue.");
          return null;
        }
        if (complete) await api.updateCompanyProfile(id, { ...payload, complete: true });
      }
      return id || null;
    } catch (e: any) {
      setErr(e?.message || "Couldn't save right now.");
      return null;
    } finally { saving.current = false; setBusy(false); }
  }

  async function next() {
    // [business.taxonomy-and-readiness](../../../docs/product/businesses.md#business-taxonomy-and-readiness): identify missing required information on its own step, before saving or moving on.
    const missing = profileIssue(form, tax!, retainedSubtype, step);
    if (missing) { setIssue(missing); return; }
    // Save progress moving forward; a failed save keeps the user on the step.
    const id = await persist(false);
    if (id) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }
  function goToStep(value: number) { setStep(value); setErr(null); setIssue(null); }
  function back() { goToStep(Math.max(step - 1, 0)); }

  const retainedSubtype = savedSubtype.organizationType === form.organizationType ? savedSubtype.subtype : "";
  const completionIssue = profileIssue(form, tax, retainedSubtype);
  const completionError = completionIssue?.message || null;

  async function finish() {
    if (submitting.current) return;
    if (completionError) { setErr(completionError); if (editId && mobileViewport) setIssue(completionIssue); return; }
    if (needsProof) {
      const error = proofType.trim().length < 2 ? "Enter a registration type (at least 2 characters)." : companyProofError(proofFile);
      setProofError(error);
      if (error) { section.current?.querySelector<HTMLElement>('[aria-label="Business verification"] h2')?.focus(); return; }
    }
    submitting.current = true;
    try {
      const id = await persist(true);
      if (!id) return;
      setBusy(true);
      if (needsProof && proofFile) {
        const submissionId = proofSubmissionId || crypto.randomUUID();
        setProofSubmissionId(submissionId);
        draft.checkpoint({ ...snapshot, companyId: id, proofSubmissionId: submissionId });
        await api.submitCompanyLicense(id, { licenseType: proofType.trim() }, proofFile, submissionId);
      }
      draft.clear(); navigate("/dashboard?view=companies");
    } catch (error: unknown) {
      setErr(error instanceof Error ? error.message : "Could not submit verification. Your details and proof are still here; please retry.");
    } finally { submitting.current = false; setBusy(false); }
  }

  async function saveDraft() {
    const id = await persist(false);
    if (id) navigate(editId ? "/dashboard" : returnTo);
  }

  const stepProps = { tax, form, set, issue };
  // [business.draft-and-submit](../../../docs/product/businesses.md#business-draft-and-submit): an older draft keeps its enrichment editors and media recovery;
  // new registrations defer these fields until the owner edits the profile.
  const showExtras = Boolean(editId) || legacyExtras;
  const cover = <CompanyCoverField companyId={companyId} file={coverFile} existingUrl={existingCoverUrl} removed={coverRemoved} onChange={chooseCover} onRemove={() => setCoverRemoved(true)} onUndo={() => setCoverRemoved(false)} />;
  const basics = <StepBasics {...stepProps} companyId={companyId} showExtras={showExtras} cover={cover} logoFile={logoFile} setLogoFile={chooseLogo} />;
  const location = <StepLocation {...stepProps} showExtras={showExtras} locationDraft={locationDraft} setLocationDraft={setLocationDraft} />;
  const profile = <StepProfile {...stepProps} showExtras={showExtras} retainedSubtype={retainedSubtype} selectedType={selectedType} subtypeOther={subtypeOther} setSubtypeOther={setSubtypeOther} />;
  const roles = <StepRoles {...stepProps} />;
  const industry = <StepIndustry {...stepProps} showExtras={showExtras} pendingTokens={pendingTokens} setPendingTokens={setPendingTokens} />;

  // The selected mobile edit layout exposes the same controlled fields together.
  // Creation and desktop keep the staged wizard; persistence and proof checks
  // remain owned by the existing save/finish handlers above.
  if (editId && mobileViewport) return (
    <div className="company-edit-mobile enhanced-form-fields">
      <Link href={`/company/${editId}/dashboard`} className="cm-back"><ChevronLeft />My company</Link>
      <h1>Edit Profile</h1>
      <section ref={section} aria-label="Edit company profile">
        {err && <p role="alert" className="cm-error rounded-lg bg-brand-red/10 px-4 py-3 text-sm text-brand-red">{err}</p>}
        <fieldset disabled={busy}>
          <section className="cm-panel"><h2>Business details</h2><div className="cm-fields">
            <StepBasics {...stepProps} part="details" companyId={companyId} showExtras={showExtras} cover={cover} logoFile={logoFile} setLogoFile={chooseLogo} />
            <StepProfile {...stepProps} part="details" showExtras={showExtras} retainedSubtype={retainedSubtype} selectedType={selectedType} subtypeOther={subtypeOther} setSubtypeOther={setSubtypeOther} />
          </div></section>
          <section className="cm-panel"><h2>Branding</h2><div className="cm-fields"><StepBasics {...stepProps} part="branding" companyId={companyId} showExtras={showExtras} cover={cover} logoFile={logoFile} setLogoFile={chooseLogo} /></div></section>
          <section className="cm-panel"><h2>Location &amp; office</h2><div className="cm-fields">{location}</div></section>
          <section className="cm-panel"><h2>Roles &amp; industry</h2><div className="cm-fields">{roles}<StepIndustry {...stepProps} part="industry" showExtras={showExtras} pendingTokens={pendingTokens} setPendingTokens={setPendingTokens} /></div></section>
          <section className="cm-panel"><h2>Additional offerings</h2><div className="cm-fields"><StepIndustry {...stepProps} part="offerings" showExtras={showExtras} pendingTokens={pendingTokens} setPendingTokens={setPendingTokens} /></div></section>
          <section className="cm-panel"><h2>Business classifications</h2><div className="cm-fields"><StepProfile {...stepProps} part="classification" showExtras={showExtras} retainedSubtype={retainedSubtype} selectedType={selectedType} subtypeOther={subtypeOther} setSubtypeOther={setSubtypeOther} /></div></section>
          {needsProof && <div className="cm-proof"><CompanyVerificationFields type={proofType} file={proofFile} suggestions={selectedType?.verification || []} error={proofError} onType={value => { setProofType(value); setProofSubmissionId(""); setProofError(null); }} onFile={file => { setProofFile(file); setProofSubmissionId(""); setProofError(file ? companyProofError(file) : null); }} /></div>}
        </fieldset>
      </section>
      <div className="cm-actions">
        <button type="button" onClick={saveDraft} disabled={busy} className="btn-outline disabled:opacity-60">Save draft</button>
        <button type="button" onClick={finish} disabled={busy} className="btn-red disabled:opacity-60">{busy ? "Saving…" : needsProof ? "Submit for verification" : "Save changes"}</button>
      </div>
    </div>
  );

  return (
    <div className="container-x py-6 pb-24 enhanced-form-fields">
      <div className="max-w-4xl mx-auto">
      {!editId && <p role="status" className={`text-sm mb-3 ${draft.warning ? "text-brand-red" : "text-mut"}`}>{draft.warning || draft.message}</p>}
      {editId && <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-navy">Edit Business Profile</h1>
        <p className="text-mut text-sm mt-1">Set up how your business appears on KontrataPH. You can save and finish later.</p>
      </div>}

      <CompanyProgress step={step} disabled={busy} onStep={goToStep} />
        <section ref={section} aria-label={STEPS[step].t}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-mut"><span>Step {step + 1} of {STEPS.length} · {STEPS[step].d}</span><span>Fields marked * are required</span></div>
          {err && <div role="alert" className="mb-4 rounded-lg bg-brand-red/10 text-brand-red text-sm px-4 py-2.5">{err}</div>}
          <fieldset disabled={busy} className="min-w-0">
          {step === 0 && <div className="card p-5 sm:p-6 space-y-6">
            {basics}
            <div className="border-t border-line pt-5">{profile}</div>
            <div className="border-t border-line pt-5">{location}</div>
          </div>}
          {step === 1 && <div className="card p-5 sm:p-6 space-y-6">
            {roles}
            <div className="border-t border-line pt-5">{industry}</div>
          </div>}
          {step === 2 && <>
            <StepReview tax={tax} form={form} showExtras={showExtras} selectedType={selectedType} subtypeOther={subtypeOther} disabled={busy} coverName={coverFile?.name || (coverRemoved || !existingCoverUrl ? "Default industry image" : "Current cover photo")} logoName={logoFile?.name || "No new logo selected"} editors={{
              basics, location, profile, roles, industry,
            }} />
            {completionIssue && <p role="alert" className="mt-3 text-sm text-brand-red">{completionError} <button type="button" className="underline font-semibold" onClick={() => goToStep(completionIssue.step)}>Go to {STEPS[completionIssue.step].t}</button></p>}
            {needsProof ? <CompanyVerificationFields type={proofType} file={proofFile} suggestions={selectedType?.verification || []} error={proofError} onType={value => { setProofType(value); setProofSubmissionId(""); setProofError(null); }} onFile={file => { setProofFile(file); setProofSubmissionId(""); setProofError(file ? companyProofError(file) : null); }} /> : <p className="mt-4 text-sm text-mut">Your registration has already been submitted. Track its status in My Company.</p>}
          </>}
          </fieldset>

          {/* Nav */}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-line">
            <button onClick={back} disabled={step === 0 || busy}
              className="btn-ghost h-10 px-4 disabled:opacity-40"><ChevronLeft className="w-4 h-4" />Back</button>
            <div className="flex gap-2">
              <button onClick={saveDraft} disabled={busy} className="btn-outline h-10 px-4 disabled:opacity-60">Save draft</button>
              {step < STEPS.length - 1
                ? <button onClick={next} disabled={busy} className="btn-red h-10 px-5 disabled:opacity-60">{busy ? "Saving…" : "Continue"}<ChevronRight className="w-4 h-4" /></button>
                : <button onClick={finish} disabled={busy || Boolean(completionError)} className="btn-red h-10 px-5 disabled:opacity-60"><ShieldCheck className="w-4 h-4" />{busy ? "Saving…" : needsProof ? "Submit for verification" : "Save changes"}</button>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );

}
